// Global explicit-subscription-approval banner. Mounted on authed pages; polls
// /approvals/count every 3s. When there are pending requests it shows a single
// dismissible bar ("You have N pending Approvals") linking to /approvals, and
// auto-dismisses 15s after it last appeared. Dismissing hides it until the
// pending count changes (a NEW request re-shows it).
import { h, icon } from './dom.js';
import { api } from '../api/client.js';
import { approvalsBannerText } from './nav.js';
export { approvalsBannerText } from './nav.js';

const POLL_MS = 3000;
const AUTO_DISMISS_MS = 15000;

let mounted = false;
let pollTimer: number | undefined;
let dismissTimer: number | undefined;
let container: HTMLElement | null = null;

// Last count we rendered a banner for; used so a dismiss stays dismissed until
// the count changes (e.g. a brand-new request arrives).
let dismissedForCount = 0;
let lastCount = 0;

function hide(): void {
  if (!container) return;
  container.style.display = 'none';
  container.replaceChildren();
  if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = undefined; }
}

function show(count: number): void {
  if (!container) return;
  container.style.display = 'flex';
  container.replaceChildren(
    icon('<path d="M9 12l2 2 4-4"/><path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>', 20),
    h('div', { style: 'flex:1;min-width:0;font-weight:800' }, approvalsBannerText(count)),
    h('a', { class: 'btn btn-primary btn-sm', href: '#/approvals',
      onclick: () => { dismissedForCount = count; hide(); } }, 'Review'),
    h('button', { class: 'btn btn-sm', type: 'button', 'aria-label': 'Dismiss',
      onclick: () => { dismissedForCount = count; hide(); } }, 'Dismiss'),
  );
  // Auto-dismiss after 15s (reset the timer each time we (re)show).
  if (dismissTimer) clearTimeout(dismissTimer);
  dismissTimer = window.setTimeout(() => { dismissedForCount = count; hide(); }, AUTO_DISMISS_MS);
}

async function refresh(): Promise<void> {
  const count = await api.approvalsCount().catch(() => -1);
  if (count < 0) return; // transient error; keep current state
  // A change in count means new activity → allow the banner to re-appear.
  if (count !== lastCount) {
    lastCount = count;
    if (count === 0) { dismissedForCount = 0; hide(); return; }
    // New/changed pending set: un-dismiss unless we already dismissed THIS count.
    if (count !== dismissedForCount) show(count);
    return;
  }
  if (count === 0) { hide(); return; }
  // Same non-zero count: keep showing unless dismissed.
  if (count !== dismissedForCount && container && container.style.display === 'none' && !dismissTimer) {
    // don't spontaneously re-show a dismissed same-count banner
  }
}

/** mountApprovalsBanner installs the sticky approvals bar + starts the 3s poll. */
export function mountApprovalsBanner(): void {
  const hash = (location.hash || '').replace(/^#/, '');
  if (hash.startsWith('/login') || hash.startsWith('/join')) {
    unmountApprovalsBanner();
    return;
  }
  if (!container) {
    container = h('div', {
      id: 'vid-approvals-banner',
      style: 'position:sticky;top:0;z-index:59;display:none;align-items:center;gap:12px;padding:10px 16px;background:var(--accent-soft,#eef4ff);border:1px solid var(--accent,#3b82f6);border-radius:12px;margin:10px 16px 0',
    });
  }
  const app = document.getElementById('app');
  if (app && container.parentElement !== app) {
    // Place directly after the join banner (or first) so both stack cleanly.
    const join = document.getElementById('vid-join-banner');
    if (join && join.nextSibling) app.insertBefore(container, join.nextSibling);
    else app.insertBefore(container, app.firstChild);
  }
  if (!mounted) {
    mounted = true;
    void refresh();
    pollTimer = window.setInterval(() => void refresh(), POLL_MS);
  }
}

/** unmountApprovalsBanner tears down timers + DOM (logout / public routes). */
export function unmountApprovalsBanner(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = undefined; }
  if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = undefined; }
  mounted = false;
  lastCount = 0;
  dismissedForCount = 0;
  if (container?.parentElement) container.parentElement.removeChild(container);
  container = null;
}
